/**
 * Tipos para personalização de tema do evento (Frontend)
 */

export interface ThemeCustomization {
    colors?: {
        backgroundColor?: string;
        secondaryBackgroundColor?: string;
        primaryColor?: string;
        primaryColorHover?: string;
        textColor?: string;
        textSecondaryColor?: string;
        linkColor?: string;
        borderColor?: string;
        gradient?: {
            start?: string;
            middle?: string;
            end?: string;
        };
    };
    typography?: {
        fontFamily?: string;
        baseFontSize?: string;
    };
    spacing?: {
        sectionPadding?: string;
    };
    borderRadius?: {
        default?: string;
    };
}

export const DEFAULT_THEME: ThemeCustomization = {
    colors: {
        backgroundColor: '#F5F5F5',
        secondaryBackgroundColor: '#F5F5F5',
        primaryColor: '#1565C0',
        textColor: '#222222',
        textSecondaryColor: '#424242',
    },
    typography: {
        fontFamily: 'Manrope, sans-serif',
    },
};

/**
 * Mescla tema personalizado com tema padrão
 */
export function mergeTheme(
    customTheme?: ThemeCustomization,
    defaultTheme: ThemeCustomization = DEFAULT_THEME
): ThemeCustomization {
    if (!customTheme) {
        return defaultTheme;
    }

    return {
        colors: {
            ...defaultTheme.colors,
            ...customTheme.colors,
            gradient: {
                ...defaultTheme.colors?.gradient,
                ...customTheme.colors?.gradient,
            },
        },
        typography: {
            ...defaultTheme.typography,
            ...customTheme.typography,
        },
        spacing: {
            ...defaultTheme.spacing,
            ...customTheme.spacing,
        },
        borderRadius: {
            ...defaultTheme.borderRadius,
            ...customTheme.borderRadius,
        },
    };
}

/**
 * Gera CSS variables a partir do tema
 */
export function generateThemeCSSVariables(theme: ThemeCustomization): Record<string, string> {
    const cssVars: Record<string, string> = {};

    if (theme.colors) {
        if (theme.colors.backgroundColor) {
            cssVars['--event-bg-color'] = theme.colors.backgroundColor;
        }
        if (theme.colors.secondaryBackgroundColor) {
            cssVars['--event-secondary-bg-color'] = theme.colors.secondaryBackgroundColor;
        }
        if (theme.colors.primaryColor) {
            cssVars['--event-primary-color'] = theme.colors.primaryColor;
        }
        if (theme.colors.primaryColorHover) {
            cssVars['--event-primary-color-hover'] = theme.colors.primaryColorHover;
        }
        if (theme.colors.textColor) {
            cssVars['--event-text-color'] = theme.colors.textColor;
        }
        if (theme.colors.textSecondaryColor) {
            cssVars['--event-text-secondary-color'] = theme.colors.textSecondaryColor;
        }
        if (theme.colors.linkColor) {
            cssVars['--event-link-color'] = theme.colors.linkColor;
        }
        if (theme.colors.borderColor) {
            cssVars['--event-border-color'] = theme.colors.borderColor;
        }
        if (theme.colors.gradient) {
            if (theme.colors.gradient.start) {
                cssVars['--event-gradient-start'] = theme.colors.gradient.start;
            }
            if (theme.colors.gradient.middle) {
                cssVars['--event-gradient-middle'] = theme.colors.gradient.middle;
            }
            if (theme.colors.gradient.end) {
                cssVars['--event-gradient-end'] = theme.colors.gradient.end;
            }
        }
    }

    if (theme.typography?.fontFamily) {
        cssVars['--event-font-family'] = theme.typography.fontFamily;
    }

    if (theme.typography?.baseFontSize) {
        cssVars['--event-base-font-size'] = theme.typography.baseFontSize;
    }

    return cssVars;
}
